import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, RefreshCw, Target } from "lucide-react";

// Cost model for Gemini 3 Flash via Lovable AI Gateway (USD per 1M tokens).
// Kept as a constant here so the KPI reflects the same rate used for budgeting.
const COST_PER_M_IN = 0.1;
const COST_PER_M_OUT = 0.4;

type WindowKey = "7d" | "30d" | "90d";
const WINDOW_DAYS: Record<WindowKey, number> = { "7d": 7, "30d": 30, "90d": 90 };

type Kpi = {
  key: string;
  label: string;
  value: string;
  target: string;
  hit: boolean | null;
  source: string;
};

function pct(n: number) { return `${(n * 100).toFixed(1)}%`; }
function ms(n: number) { return n < 1000 ? `${Math.round(n)}ms` : `${(n / 1000).toFixed(2)}s`; }
function usd(n: number) { return `$${n.toFixed(4)}`; }
function median(xs: number[]) {
  if (!xs.length) return 0;
  const s = [...xs].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
}

export function HelpdeskKpiPanel() {
  const [win, setWin] = useState<WindowKey>("30d");
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<{
    conversations: number;
    escalations: number;
    downvoted_convs: Set<string>;
    upvotes: number;
    downvotes: number;
    latencies: number[];
    tokensIn: number;
    tokensOut: number;
    resolvedConvs: number;
    escalationCreates: number;
    escalationDenied: number;
  } | null>(null);

  const load = async () => {
    setLoading(true);
    const since = new Date(Date.now() - WINDOW_DAYS[win] * 86400_000).toISOString();

    const [tel, tickets, fb, audit] = await Promise.all([
      supabase.from("helpdesk_telemetry")
        .select("conversation_id, latency_ms, tokens_in, tokens_out, created_at")
        .gte("created_at", since).limit(10000),
      supabase.from("support_tickets")
        .select("id, created_at").gte("created_at", since).limit(10000),
      supabase.from("helpdesk_feedback")
        .select("conversation_id, helpful, created_at")
        .gte("created_at", since).limit(10000),
      supabase.from("helpdesk_tool_audit")
        .select("tool_name, outcome, created_at")
        .eq("tool_name", "create_ticket")
        .gte("created_at", since).limit(10000),
    ]);

    const telRows = (tel.data ?? []) as Array<{ conversation_id: string | null; latency_ms: number | null; tokens_in: number | null; tokens_out: number | null }>;
    const fbRows = (fb.data ?? []) as Array<{ conversation_id: string | null; helpful: boolean }>;
    const auditRows = (audit.data ?? []) as Array<{ outcome: string }>;

    const escalatedConvs = new Set<string>();
    // A conversation is "escalated" if it produced a support ticket in-window.
    // Ticket count is a reasonable proxy since create_ticket writes to support_tickets.
    const ticketCount = tickets.data?.length ?? 0;

    const convIds = new Set(telRows.map((r) => r.conversation_id).filter(Boolean) as string[]);
    const downvoted = new Set<string>();
    let up = 0, down = 0;
    for (const r of fbRows) {
      if (r.helpful) up++; else {
        down++;
        if (r.conversation_id) downvoted.add(r.conversation_id);
      }
    }

    const latencies = telRows.map((r) => r.latency_ms ?? 0).filter((n) => n > 0);
    const tokensIn = telRows.reduce((n, r) => n + (r.tokens_in ?? 0), 0);
    const tokensOut = telRows.reduce((n, r) => n + (r.tokens_out ?? 0), 0);

    setData({
      conversations: convIds.size,
      escalations: ticketCount,
      downvoted_convs: downvoted,
      upvotes: up,
      downvotes: down,
      latencies,
      tokensIn,
      tokensOut,
      resolvedConvs: Math.max(0, convIds.size - downvoted.size),
      escalationCreates: auditRows.filter((r) => r.outcome === "ok").length,
      escalationDenied: auditRows.filter((r) => r.outcome === "denied" || r.outcome === "error").length,
    });
    setLoading(false);
  };

  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [win]);

  const kpis = useMemo<Kpi[]>(() => {
    if (!data) return [];
    const totalFb = data.upvotes + data.downvotes;
    const csat = totalFb ? data.upvotes / totalFb : 0;
    const escalationRate = data.conversations ? data.escalations / data.conversations : 0;
    const deflection = 1 - escalationRate;
    const fcr = data.conversations ? (data.conversations - data.escalations) / data.conversations : 0;
    const medLatency = median(data.latencies);
    const totalAuditEscalations = data.escalationCreates + data.escalationDenied;
    const escalationAccuracy = totalAuditEscalations ? data.escalationCreates / totalAuditEscalations : 1;
    const cost = (data.tokensIn / 1_000_000) * COST_PER_M_IN + (data.tokensOut / 1_000_000) * COST_PER_M_OUT;
    const costPerResolved = data.resolvedConvs ? cost / data.resolvedConvs : 0;

    return [
      { key: "deflect", label: "Ticket deflection rate", value: pct(deflection), target: "≥ 60%", hit: deflection >= 0.6, source: "tickets vs. conversations" },
      { key: "fcr", label: "First-contact resolution", value: pct(fcr), target: "≥ 75%", hit: fcr >= 0.75, source: "sessions w/o escalation" },
      { key: "csat", label: "CSAT (👍 rate)", value: totalFb ? pct(csat) : "—", target: "≥ 85%", hit: totalFb ? csat >= 0.85 : null, source: `helpdesk_feedback (${totalFb})` },
      { key: "lat", label: "Median time-to-answer", value: data.latencies.length ? ms(medLatency) : "—", target: "< 4s", hit: data.latencies.length ? medLatency < 4000 : null, source: "edge function telemetry" },
      { key: "hallu", label: "Hallucination rate", value: "manual", target: "< 1%", hit: null, source: "weekly sampled review" },
      { key: "esc", label: "Escalation accuracy", value: totalAuditEscalations ? pct(escalationAccuracy) : "—", target: "≥ 90%", hit: totalAuditEscalations ? escalationAccuracy >= 0.9 : null, source: "helpdesk_tool_audit" },
      { key: "cost", label: "Cost per resolved session", value: data.resolvedConvs ? usd(costPerResolved) : "—", target: "< $0.02", hit: data.resolvedConvs ? costPerResolved < 0.02 : null, source: "Lovable AI tokens × rate" },
    ];
  }, [data]);

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Target className="size-4 text-primary" />
            Helpdesk KPIs
          </CardTitle>
          <CardDescription>
            6-month targets tracked against live data. Hallucination rate is filled in manually from the weekly RAG eval review.
          </CardDescription>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-md border border-border overflow-hidden">
            {(Object.keys(WINDOW_DAYS) as WindowKey[]).map((k) => (
              <button
                key={k}
                onClick={() => setWin(k)}
                className={`px-2.5 py-1 text-xs ${win === k ? "bg-primary text-primary-foreground" : "bg-background hover:bg-accent"}`}
              >
                {k}
              </button>
            ))}
          </div>
          <Button variant="ghost" size="icon-sm" onClick={() => void load()} aria-label="Refresh">
            {loading ? <Loader2 className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase text-muted-foreground border-b border-border">
                <th className="py-2 pr-4 font-medium">KPI</th>
                <th className="py-2 pr-4 font-medium">Value</th>
                <th className="py-2 pr-4 font-medium">Target</th>
                <th className="py-2 pr-4 font-medium">Status</th>
                <th className="py-2 font-medium">Source</th>
              </tr>
            </thead>
            <tbody>
              {kpis.map((k) => (
                <tr key={k.key} className="border-b border-border/50 last:border-0">
                  <td className="py-2 pr-4">{k.label}</td>
                  <td className="py-2 pr-4 font-mono tabular-nums">{k.value}</td>
                  <td className="py-2 pr-4 text-muted-foreground font-mono text-xs">{k.target}</td>
                  <td className="py-2 pr-4">
                    {k.hit === null ? (
                      <Badge variant="secondary">n/a</Badge>
                    ) : k.hit ? (
                      <Badge className="bg-primary/15 text-primary hover:bg-primary/20">on target</Badge>
                    ) : (
                      <Badge variant="destructive">below</Badge>
                    )}
                  </td>
                  <td className="py-2 text-xs text-muted-foreground">{k.source}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-[11px] text-muted-foreground">
          Cost model: {`$${COST_PER_M_IN}/M in + $${COST_PER_M_OUT}/M out`} (Gemini 3 Flash via Lovable AI). Update in <code>HelpdeskKpiPanel.tsx</code> if rates change.
        </p>
      </CardContent>
    </Card>
  );
}

export default HelpdeskKpiPanel;
